import {IReg, IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import "/@back@/core/dialogs/dialogs_Perms.js";
import {ActionWrapperRefresRolesExplorer, IUsersRolesActionCtx, JRoleExpEntrySetAccount, JRoleExpEntryStructLevel, ORolesExplorerTabInit, RolesExplorerTabBase, RoleUiHandlerContextUsed} from "/@back@/core/dialogs/rolesExplorer.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {JInfoWsp, JNodeRolesSet, JWspProviderRolesSet, JWspRolesSet, Wsp, WSP, WSPMETA} from "/@lib@/wsp/wsp.js";
import {srcUri} from "/@lib@/wsp/src.js";
import {InputUserPanel, OInputUserInit} from "/@back@/core/widgets/inputs.js";
import {EUserAspects} from "/@lib@/core/user.js";
import {OUserRefInit, UserRef} from "/@back@/core/widgets/userRef.js";
import {IRoleEditUiHandlerContext, RoleEditUiHandler, ROLES} from "/@lib@/commons/roles.js";
import {ISetPermsSpaceCtx, SetPermsSpace} from "/@back@/wsp/actions/srcActions.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {IShortDescCtx} from "/@lib@/wsp/item.js";
import {IOpenPermsWspActionCtx, IWspActionCtx, OpenPermsWspAction} from "/@back@/wsp/actions/wspActions.js";
import {ICoreUniverseEnv} from "/@lib@/core/universe.js";
import {IOpenUsersMgrCtx, OpenGroupsMgr, OpenUsersMgr} from "/@back@/core/plugins/usersPlg.js";
import {MsgLabel} from "/@back@/commons/basis.js";


interface JRoleExpEntryStructLevelRepo<R extends JRoleExpEntryStructLevelRepo<R> | JRoleExpEntrySetAccount> extends JRoleExpEntryStructLevel<R> {
	structLevel: 'roles' | 'repo' | 'wsp' | 'space'
	/* reposUri : wsp/srcUri(@account) */
	uid: string
	wspCode?: string
	ch?: R[]
}

export interface OWspRolesExplorerTabInit extends ORolesExplorerTabInit {
	reg: IReg<IChainEnv & IUiEnv>
}


export class WspRolesExplorerTab<E extends IRegPointer<IChainEnv & IUiEnv>, R extends (JRoleExpEntryStructLevelRepo<R> | JRoleExpEntrySetAccount), CTX extends IUsersRolesActionCtx<E, R, CTX>> extends RolesExplorerTabBase<E, R, CTX> {
	reg: IReg<IChainEnv & IUiEnv>;

	wspMap: Map<string, Wsp> = new Map();

	constructor() {
		super();
		this.id = "wsp";
	}

	protected async _initialize(init: OWspRolesExplorerTabInit): Promise<void> {
		if (!("filterTxtPlaceholder" in init)) init.filterTxtPlaceholder = "Ateliers/espaces...";
		await super._initialize(init);
	}


	/** Build les rolesUiHandler d'aprés les wsps issus du **dernier** doFetchDatas */
	protected async rolesUiHandlers(): Promise<(RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed)[]> {
		let uiHandlers = await Promise.all(this.reg.mergeLists(ROLES.UI_ROLES_LIST)) as (RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed)[];
		// Recherche des UiHandlers des wsps
		await Promise.all(Array.from(this.wspMap.values()).map(async (wsp) => {
			const list = await Promise.all(wsp.reg.mergeLists(ROLES.UI_ROLES_LIST)) as (RoleEditUiHandler<IRoleEditUiHandlerContext>)[];
			list.forEach(uiHandler => {
				const declaredUiHandler = uiHandlers.find(test => test.roleCode === uiHandler.roleCode);
				if (declaredUiHandler) {
					if ((declaredUiHandler as RoleUiHandlerContextUsed).whiteListLevelsUid)
						(declaredUiHandler as RoleUiHandlerContextUsed).whiteListLevelsUid.push(wsp.code);
				} else {
					(uiHandler as RoleUiHandlerContextUsed).whiteListLevelsUid = [wsp.code];
					uiHandlers.push(uiHandler);
				}
			});
		}));
		return uiHandlers;
	}

	/** Seuil à partir duquel la liste des roles affectés à un niveau devient repliable */
	static EXTENDED_ROLES_GAP = 10;

	async doFetchDatas(): Promise<R[]> {
		const assignedRoles = await WSP.fetchExportAssignedRoles(this.reg.env.universe.wspServer, {fields: ["srcRoles", "srcRi"]});

		function filterWspLine(wspLine: JWspRolesSet): boolean {
			return (wspLine.nodes?.roles || wspLine.nodes?.ch) ? true : false;
		}

		// - Création de la structure de donnée grid
		const root: R[] = [];

		function injectRoles(target: R[], from: JWspProviderRolesSet | JNodeRolesSet, fullUri: string, title = "Rôles affectés") {
			if (from.roles) {
				const rolesArray = Object.entries(from.roles);
				const structRolesLevel: JRoleExpEntryStructLevelRepo<R> = rolesArray.length >= WspRolesExplorerTab.EXTENDED_ROLES_GAP ? {structLevel: "roles", title: title, ch: [], uid: `${fullUri}@`} : null;
				for (const [account, value] of rolesArray) {
					const entryFullUri = `${fullUri}@${account}`;
					const accountEntry: JRoleExpEntrySetAccount = {account, uid: entryFullUri};
					if (value.allowed)
						accountEntry.allowed = value.allowed;
					if (value.denied)
						accountEntry.denied = value.denied;
					if (value.userUnknown) accountEntry.userUnknown = true;

					if (structRolesLevel)
						structRolesLevel.ch.push(accountEntry as R);
					else
						target.push(accountEntry as R);
				}
				if (structRolesLevel) target.push(structRolesLevel as R);
			}
		}

		function injectChildren(target: JRoleExpEntryStructLevelRepo<R>[], from: JNodeRolesSet[], fullUri: string) {
			if (from) {
				for (const entry of from) {
					const entryFullUri = `${fullUri}/${entry.name}`
					const structLevel: JRoleExpEntryStructLevelRepo<R> = {structLevel: "space", title: entry.name, uid: entryFullUri, ch: []};
					injectRoles(structLevel.ch, entry, entryFullUri);
					injectChildren(structLevel.ch as JRoleExpEntryStructLevelRepo<R>[], entry.ch, entryFullUri);
					target.push(structLevel);
				}
			}
		}

		const repoLevel: JRoleExpEntryStructLevelRepo<R> = {structLevel: "repo", title: "Entrepôt", ch: [], uid: ""};
		root.push(repoLevel as R);
		injectRoles(repoLevel.ch, assignedRoles, "", "Rôles affectés sur l'entrepôt");
		if (assignedRoles.wsps) {
			const wspMetasCache = new WSPMETA.WspMetasCache(this.reg.env.universe.wspServer);
			for (const [wspCode, value] of Object.entries(assignedRoles.wsps)) {
				if (filterWspLine(value))
					wspMetasCache.fetchWspMetaUi(wspMetasCache.atomWspTypeInst(value.infoWsp));
			}
			await wspMetasCache.waitForAll();
			for (const [wspCode, value] of Object.entries(assignedRoles.wsps)) {
				if (filterWspLine(value)) {
					const wspMetaUi = wspMetasCache.getWspMetaUi(value.infoWsp);
					this.wspMap.set(wspCode, new Wsp(this.reg.env.universe.wspServer, wspCode, value.infoWsp as JInfoWsp, wspMetaUi));
					const structLevel: JRoleExpEntryStructLevelRepo<R> = {structLevel: "wsp", title: value.title || wspCode, uid: wspCode, wspCode: wspCode, ch: []};
					injectRoles(structLevel.ch, value.nodes, wspCode);
					injectChildren(structLevel.ch as JRoleExpEntryStructLevelRepo<R>[], value.nodes.ch, wspCode);
					repoLevel.ch.push(structLevel as R);
				}
			}
		}

		return root;
	}


	async buildRowDetails(row: R): Promise<HTMLElement> {
		if (row) {
			const inElt = <div/>;
			const account = (row as JRoleExpEntrySetAccount).account;
			let wspCode: string;
			let wspTitle: string;
			let srcUri: srcUri;
			let parentStructLevel: JRoleExpEntryStructLevelRepo<R> = ("account" in (row as JRoleExpEntrySetAccount) ? this.dataHolder.parent(row) : row) as JRoleExpEntryStructLevelRepo<R>;
			while (parentStructLevel) {
				if (parentStructLevel.structLevel === "space") {
					srcUri = parentStructLevel.title + (srcUri ? `/${srcUri}` : '');
					parentStructLevel = this.dataHolder.parent(parentStructLevel as R) as JRoleExpEntryStructLevelRepo<R>;
				} else {
					if (parentStructLevel.structLevel === "wsp") {
						wspCode = parentStructLevel.wspCode;
						wspTitle = parentStructLevel.title;
					}
					break;
				}
			}
			if (srcUri) srcUri = `/${srcUri}`;
			let wsp: Wsp = this.wspMap.get(wspCode);

			const accountWidget = new InputUserPanel().initialize({
				name: "filterAccounts",
				reg: this.reg,
				emptySelectionMsg: "Sélectionner un utilisateur/groupe...",
				required: true,
				userCard: "single",
				hideRemButton: true,
				usersGridInit: {
					usersSrv: this.usersSrv,
					filterType: null,
					filterTypeInputVisibility: true,
				},
			} as OInputUserInit<E>) as InputUserPanel<E>;
			await accountWidget.initializedAsync;
			DOM.addClass(accountWidget, "formElt");
			if (account) accountWidget.value = [account];
			else if (this.filterAccounts.value)
				accountWidget.value = this.filterAccounts.value;

			let detailsBarActions;

			if(wspCode && !wsp){
				const wspTitle = wspCode;
				inElt.appendChild(new MsgLabel().initialize({reg: this.reg, label: `Atelier '${wspTitle}' non disponible`, level:"error"}));
				return inElt;
			}

			if (srcUri) {
				// - srcUri
				const node = await WSP.fetchShortDescTree(wsp, this, srcUri);
				detailsBarActions = inElt.appendChild(new BarActions<IShortDescCtx & ISetPermsSpaceCtx & IUsersRolesActionCtx<E, R, CTX>>().initialize({
					reg: wsp.reg,
					actions: [
						new ActionWrapperRefresRolesExplorer().setOverridenSvc(new SetPermsSpace())
					],
					actionContext: {
						rolesExplorer: this,
						reg: wsp.reg,
						emitter: this,
						shortDescs: [node],
						preSelectAccounts: (account ? [account] : null),
					},
					uiContext: "bar",
					disableFullOverlay: true,
				})) as BarActions<IShortDescCtx & ISetPermsSpaceCtx & IUsersRolesActionCtx<E, R, CTX>>;

				inElt.appendChild(<div class="header">Rôles résolus de {accountWidget} sur l'espace <span class="refUri">'{srcUri}'</span> de l'atelier <span class="refWsp">'{wsp.wspTitle}'</span> :</div>);
			} else if (wsp) {
				detailsBarActions = inElt.appendChild(new BarActions<IWspActionCtx & IOpenPermsWspActionCtx & IUsersRolesActionCtx<E, R, CTX>>().initialize({
					reg: wsp.reg,
					actions: [
						new ActionWrapperRefresRolesExplorer().setOverridenSvc(OpenPermsWspAction.SINGLETON)
					],
					actionContext: {
						rolesExplorer: this,
						reg: wsp.reg,
						preSelectAccounts: (account ? [account] : null),
					},
					uiContext: "bar",
					disableFullOverlay: true,
				})) as BarActions<IWspActionCtx & IOpenPermsWspActionCtx & IUsersRolesActionCtx<E, R, CTX>>;
				// - Wsp
				inElt.appendChild(<div class="header">Rôles résolus de {accountWidget} sur l'atelier <span class="refWsp">'{wsp.wspTitle}'</span> :</div>);
			} else {
				detailsBarActions = inElt.appendChild(new BarActions<IOpenUsersMgrCtx | IUsersRolesActionCtx<E, R, CTX>>().initialize({
					reg: this.reg,
					actions: [
						new ActionWrapperRefresRolesExplorer().setOverridenSvc(new OpenUsersMgr()),
						new ActionWrapperRefresRolesExplorer().setOverridenSvc(new OpenGroupsMgr()),
					],
					actionContext: {
						rolesExplorer: this,
						reg: this.reg as IReg<ICoreUniverseEnv>,
						preSelectAccount: account,
					},
					uiContext: "bar",
					disableFullOverlay: true,
				})) as BarActions<IOpenUsersMgrCtx & IUsersRolesActionCtx<E, R, CTX>>;

				// - Entrepôt
				inElt.appendChild(<div class="header">Rôles résolus de {accountWidget} sur l'entrepôt :</div>);
			}

			const computedRolesBox = inElt.appendChild(<div class="computedRoles"/>) as HTMLDivElement;

			accountWidget.addEventListener("change", async (evt) => {
				if (accountWidget.value) this.showComputedRolesFor(accountWidget.value[0], computedRolesBox, wsp, srcUri);
			});
			if (accountWidget.value) await this.showComputedRolesFor(accountWidget.value[0], computedRolesBox, wsp, srcUri);

			return inElt;
		}
	}

	private async showComputedRolesFor(account: string, inElt: HTMLDivElement, wsp?: Wsp, srcUri?: srcUri) {
		inElt.innerHTML = "";
		if (!account) return;

		let rolesElt: HTMLDivElement;
		const detailsUl = <ul/>;

		// - Rôles résolus
		let resultRoles: { role: string, name: string, level: "set" | "inherited" | "unknown" }[];
		if (wsp) {
			// # wsp / space
			if (inElt.innerHTML !== "") return;//race condition
			const rolesForAccounts = await WSP.srcGetRolesForAccounts(wsp, this, srcUri || "", [account]);
			if (inElt.innerHTML !== "") return;//race condition
			let roleUiCtx: IRoleEditUiHandlerContext = {
				reg: this.reg,
				grantedRoles: rolesForAccounts[account].allowedRoles,
				refusedRoles: rolesForAccounts[account].deniedRoles,
				inheritedRoles: rolesForAccounts[account].inheritedRoles
			}
			let roles = ROLES.resolveUserRoles(roleUiCtx);

			resultRoles = await ROLES.resolveRolesList(roles, roleUiCtx, await Promise.all(wsp.reg.mergeLists(ROLES.UI_ROLES_LIST)) as RoleEditUiHandler<IRoleEditUiHandlerContext>[]);
			/*resultRoles.sort((l1, l2) => {
				return l2.sortAlgo - l1.sortAlgo;
			});*/
			if (inElt.innerHTML !== "") return;//race condition
		} else {
			// # repo
			const rolesForAccounts = await this.reg.env.universe.adminUsers.getUserMap([account]);
			let roleUiCtx: IRoleEditUiHandlerContext = {
				reg: this.reg,
				grantedRoles: rolesForAccounts[account].grantedRoles,
				refusedRoles: rolesForAccounts[account].refusedRoles,
				inheritedRoles: rolesForAccounts[account].inheritedRoles
			}
			let roles = ROLES.resolveUserRoles(roleUiCtx);
			resultRoles = await ROLES.resolveRolesList(roles, roleUiCtx, await this.rolesUiHandlers());
			if (inElt.innerHTML !== "") return;//race condition
		}

		if (resultRoles?.length) {
			rolesElt = <div class="resolvedRoles" title="La résolution des rôles tient compte de la hiérarchie et des groupes d'appartenance"></div> as HTMLDivElement;
			resultRoles.forEach((role, pos) => {
				if (pos > 0) rolesElt.appendChild(<span class="spacer">, </span>);
				const entry = rolesElt.appendChild(<span class={`roleName role-${role.level}`}>{role.name}</span>);
				// - Title
				let roleName = role.name;
				switch (role.level) {
				case "inherited":
					entry.title = "affectation issue d'un autre niveau ou d'un groupe";
					break;
				case "unknown":
					entry.title = "rôle inconnu";
					break;
				}
			});
		}


		// - Groupes d'appartenance
		if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
			const users = await this.usersSrv.getUserMap([account], ["flattenedGroups"]);
			if (inElt.innerHTML !== "") return;//race condition
			const flattenedGroups = (users[account]?.flattenedGroups as string[])?.reverse();
			//if (users[account]?.userType === EUserType.user) flattenedGroups?.shift();// On retire l'account courant
			flattenedGroups?.shift();// On retire l'account courant
			if (flattenedGroups?.length) {
				let li = detailsUl.appendChild(<li><span class="title">Groupes d'appartenance : </span></li>);
				flattenedGroups.forEach((grp, pos) => {
					if (pos > 0) li.appendChild(<span class="spacer"> &gt; </span>);
					li.appendChild(<UserRef î={{reg: this.reg, nickOrAccount: grp, withIcon: true} as OUserRefInit}/>)
				});
			}
		}
		inElt.appendChild(rolesElt);
		inElt.appendChild(detailsUl);
	}

}


customElements.define("c-rolesexplorer-wsp", WspRolesExplorerTab);

REG.reg.registerSkin('c-rolesexplorer-wsp', 1, /* language=CSS */ `
	#details .spacer {
		color: var(--alt2-color);
		font-weight: bold;
		font-size: larger;
	}


	#details .refUri:before, #details .refWsp:before,
	#details .refUri:after, #details .refWsp:after {
		content: "'";
	}
`);

REG.reg.registerSkin('c-rolesexplorer-wsp-tree', 1, /* language=CSS */ `
	:host {
		--roles-struct-level-wsp: url('/@skin@/wsp/views/wspRolesExplorer/wsp.svg');
		--roles-struct-level-space: url('/@skin@/wsp/views/wspRolesExplorer/space.svg');
	}

	div[data-struct-level='repo'],
	div[data-struct-level='wsp'] {
		font-variant: small-caps;
		background-color: var(--inv-bgcolor);
		color: var(--inv-color);
	}

	div[data-struct-level='repo'].inSel,
	div[data-struct-level='wsp'].inSel {
		color: var(--color)
	}

	div[data-struct-level='repo'] {
		font-size: 1.3rem;
	}

	div[data-struct-level='repo'] > .cell:first-child .icon {
		padding-inline-start: unset !important;
	}

	div[data-struct-level='wsp'] {
		margin-block-start: 1rem;
		font-size: 1.2rem;
	}

`);

REG.reg.registerSkin('c-rolesexplorer-wsp-tree-details', 1, /* language=CSS */ `


`);